import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, Eye } from 'lucide-react';
import { type Payment, type Invoice } from '../lib/supabase';
import { fixedPaymentServices, fixedInvoiceServices } from '../lib/fixed-invoice-payment-services';
import { formatCurrency, formatInvoiceNumber } from '../lib/formatters';

interface EnrichedPayment extends Payment {
  invoice?: Invoice;
}

const PaymentDetail = () => {
  const [payment, setPayment] = useState<EnrichedPayment | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  useEffect(() => {
    const fetchPaymentAndInvoice = async () => {
      if (!id) {
        setError('No payment ID provided');
        setLoading(false);
        return;
      }

      try {
        // Fetch payment details
        const { data: paymentData, error: paymentError } = await fixedPaymentServices.getById(id);
        if (paymentError) throw paymentError;

        let enrichedPayment = paymentData;
        
        // Fetch related invoice if available
        if (paymentData?.invoice_id) {
          const { data: invoiceData, error: invoiceError } = await fixedInvoiceServices.getById(paymentData.invoice_id);
          if (!invoiceError && invoiceData) {
            enrichedPayment = {
              ...paymentData,
              invoice: invoiceData
            };
          }
        }

        setPayment(enrichedPayment);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to fetch payment details');
      } finally {
        setLoading(false);
      }
    };

    fetchPaymentAndInvoice();
  }, [id]);

  const getStatusBadgeColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'success':
        return 'bg-green-100 text-green-800';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'failed':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatPaymentMethod = (method: string) => {
    switch (method.toLowerCase()) {
      case 'card':
        return 'Credit Card';
      case 'bank_transfer':
        return 'Bank Transfer';
      case 'paypal':
        return 'PayPal';
      default:
        return method;
    }
  };

  const handleApplyPayment = async () => {
    if (!payment?.invoice?.id || !payment.paid_at || payment.invoice.status === 'paid') return;

    try {
      await fixedInvoiceServices.update(payment.invoice.id, {
        status: 'paid',
        paid_at: payment.paid_at
      });

      // Update the local state
      setPayment(prev => prev ? {
        ...prev,
        invoice: {
          ...prev.invoice!,
          status: 'paid',
          paid_at: payment.paid_at
        }
      } : null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to apply payment to invoice');
    }
  };

  if (loading) return <div className="text-center p-4">Loading...</div>;
  if (error) return <div className="text-red-600 p-4">{error}</div>;
  if (!payment) return <div className="text-center p-4">Payment not found</div>;

  return (
    <div className="p-6">
      <div className="mb-6">
        <Link to="/payments" className="flex items-center text-blue-600 hover:text-blue-800">
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Payments
        </Link>
      </div>

      <div className="bg-white shadow rounded-lg">
        <div className="p-6">
          <h1 className="text-2xl font-bold mb-4">Payment Details</h1>
          
          <div className="grid grid-cols-2 gap-4 mb-6">
            <div>
              <p className="text-sm font-medium text-gray-500">Payment Amount</p>
              <p className="mt-1 text-2xl font-semibold">{formatCurrency(payment.amount)}</p>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-500">Status</p>
              <p className="mt-1">
                <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusBadgeColor(payment.status)}`}>
                  {payment.status}
                </span>
              </p>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-500">Payment Method</p>
              <p className="mt-1">{formatPaymentMethod(payment.payment_method)}</p>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-500">Payment Date</p>
              <p className="mt-1">{formatDate(payment.paid_at)}</p>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-500">Transaction ID</p>
              <p className="mt-1">{payment.transaction_id || 'N/A'}</p>
            </div>
          </div>

          {payment.invoice && (
            <div className="border-t pt-6 mt-6">
              <h2 className="text-lg font-medium mb-4 flex justify-between items-center">
                <span>Related Invoice</span>
                {payment.status === 'success' && payment.invoice.status !== 'paid' && (
                  <button 
                    onClick={handleApplyPayment}
                    className="px-4 py-2 bg-green-600 text-white text-sm rounded hover:bg-green-700"
                  >
                    Apply Payment to Invoice
                  </button>
                )}
              </h2>
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="flex justify-between items-start">
                  <div>
                    <p className="text-sm font-medium text-gray-500">Invoice #</p>
                    <div className="mt-1 flex items-center space-x-2">
                      <span className="text-gray-900">{formatInvoiceNumber(payment.invoice)}</span>
                      <Link
                        to={`/invoices/${payment.invoice.id}`}
                        className="text-blue-600 hover:text-blue-800"
                        title="View Invoice Details"
                      >
                        <Eye className="h-4 w-4 inline" />
                      </Link>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-medium text-gray-500">Amount</p>
                    <p className="mt-1 text-lg font-semibold">{formatCurrency(payment.invoice.amount)}</p>
                  </div>
                </div>
                <div className="flex justify-between items-start mt-4">
                  <div>
                    <p className="text-sm font-medium text-gray-500">Due Date</p>
                    <p className="mt-1">{formatDate(payment.invoice.due_date)}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-medium text-gray-500">Status</p>
                    <p className="mt-1">
                      <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusBadgeColor(payment.invoice.status)}`}>
                        {payment.invoice.status}
                      </span>
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PaymentDetail;
